import React, { useMemo, useState, useEffect } from 'react';
import { useTask } from '../contexts/TaskContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { FiUser, FiAlertCircle, FiCheckCircle, FiClock } from 'react-icons/fi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';

const CAPACITY = 10; // default max tasks per person threshold
const COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];

const WorkloadView = () => {
  const { filteredTasks: tasks = [], users = [] } = useTask();
  const [firestoreUsers, setFirestoreUsers] = useState({});

  // Fetch profile photos from Firestore users collection
  useEffect(() => {
    getDocs(collection(db, 'users')).then(snap => {
      const map = {};
      snap.forEach(doc => { map[doc.data().email] = doc.data(); });
      setFirestoreUsers(map);
    }).catch(() => {});
  }, []);

  const workloadData = useMemo(() => {
    // Group tasks by assignee (asignados contains emails)
    const map = {};
    tasks.forEach(task => {
      const assignees = task.asignados || [];
      if (assignees.length === 0) {
        if (!map['sin-asignar']) map['sin-asignar'] = { id: 'sin-asignar', name: 'Sin asignar', tasks: [], open: 0, done: 0, overdue: 0 };
        map['sin-asignar'].tasks.push(task);
      } else {
        assignees.forEach(email => {
          if (!map[email]) {
            const u = users.find(u => u.email === email || u.id === email || u.uid === email);
            const fsUser = firestoreUsers[email];
            const photo = fsUser?.photoURL || u?.photoURL;
            const name = fsUser?.displayName || u?.displayName || u?.name || email;
            map[email] = { id: email, name, photo, tasks: [], open: 0, done: 0, overdue: 0 };
          }
          map[email].tasks.push(task);
        });
      }
    });

    const now = new Date();
    Object.values(map).forEach(entry => {
      entry.tasks.forEach(t => {
        if (t.estado === 'finalizado') {
          entry.done++;
        } else {
          entry.open++;
          const due = t.fechaVencimiento;
          if (due) {
            const dueDate = typeof due === 'object' && due.seconds ? new Date(due.seconds * 1000) : new Date(due);
            if (dueDate < now) entry.overdue++;
          }
        }
      });
      entry.total = entry.tasks.length;
      entry.load = Math.round((entry.open / CAPACITY) * 100);
    });

    return Object.values(map).sort((a, b) => b.open - a.open);
  }, [tasks, users, firestoreUsers]);

  const chartData = useMemo(() => {
    return workloadData.map(w => ({
      name: w.name.length > 12 ? w.name.slice(0, 12) + '...' : w.name,
      fullName: w.name,
      Abiertas: w.open,
      Completadas: w.done,
      Vencidas: w.overdue,
    }));
  }, [workloadData]);

  const totalOpen = workloadData.reduce((a, w) => a + w.open, 0);
  const totalDone = workloadData.reduce((a, w) => a + w.done, 0);
  const totalOverdue = workloadData.reduce((a, w) => a + w.overdue, 0);
  const avgLoad = workloadData.length > 0 ? Math.round(totalOpen / workloadData.length) : 0;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 overflow-auto">
      {/* Header Stats */}
      <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <FiUser className="text-blue-500" size={16} />
          <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">Carga de Trabajo</h2>
          <span className="text-xs text-gray-400 ml-2">{workloadData.length} miembros</span>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Tareas abiertas', value: totalOpen, icon: FiClock, color: 'text-blue-600' },
            { label: 'Completadas', value: totalDone, icon: FiCheckCircle, color: 'text-green-600' },
            { label: 'Vencidas', value: totalOverdue, icon: FiAlertCircle, color: 'text-red-600' },
            { label: 'Promedio/persona', value: avgLoad, icon: FiUser, color: 'text-purple-600' },
          ].map((s, i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
              <s.icon className={`mx-auto mb-1 ${s.color}`} size={14} />
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-gray-500 uppercase font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 p-6 space-y-6">
        {/* Bar Chart */}
        {chartData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase mb-3">Distribucion de tareas</h3>
            <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 40)}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  formatter={(value, name) => [value, name]}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                />
                <Bar dataKey="Abiertas" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Completadas" stackId="a" fill="#22c55e" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Per-person cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {workloadData.map((w, idx) => {
            const loadPercent = Math.min(100, w.load);
            const loadColor = loadPercent > 80 ? 'bg-red-500' : loadPercent > 50 ? 'bg-amber-500' : 'bg-green-500';
            const loadLabel = loadPercent > 80 ? 'Sobrecargado' : loadPercent > 50 ? 'Ocupado' : 'Disponible';
            const loadTextColor = loadPercent > 80 ? 'text-red-600' : loadPercent > 50 ? 'text-amber-600' : 'text-green-600';

            return (
              <div key={w.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center gap-3 mb-3">
                  {w.photo ? (
                    <img src={w.photo} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: COLORS[idx % COLORS.length] }}>
                      {(w.name[0] || '?').toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{w.name}</p>
                    <p className={`text-[10px] font-medium ${loadTextColor}`}>{loadLabel}</p>
                  </div>
                </div>

                {/* Load bar */}
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
                  <div className={`h-full rounded-full transition-all ${loadColor}`} style={{ width: `${loadPercent}%` }} />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-sm font-bold text-blue-600">{w.open}</p>
                    <p className="text-[9px] text-gray-500 uppercase">Abiertas</p>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-green-600">{w.done}</p>
                    <p className="text-[9px] text-gray-500 uppercase">Listas</p>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-red-600">{w.overdue}</p>
                    <p className="text-[9px] text-gray-500 uppercase">Vencidas</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {workloadData.length === 0 && (
          <div className="text-center py-16">
            <FiUser className="mx-auto text-gray-300 mb-3" size={32} />
            <p className="text-gray-500 font-medium">No hay datos de carga</p>
            <p className="text-xs text-gray-400 mt-1">Asigna tareas a los miembros para ver la distribucion</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkloadView;
